import * as yup from 'yup';
import { createElement, forwardRef, type HTMLAttributes } from 'react';
import { cn } from '@skupreme/shared/ui';
import type { MarketplaceListItem } from 'types/hook/marketplace';

const numberFromMasked = yup
  .number()
  .transform((val, orig) =>
    orig === '' || orig == null ? undefined : Number(orig),
  );

export const returnSchema = yup.object({
  fulfillment_provider_id: yup.string().required(),
  destination: yup.object().shape({
    name: yup.string().optional(),
  }),
  itemsData: yup.object().test(value => {
    if (typeof value !== 'object' || value === null) return false;
    return Object.values(value).some(val => val === true);
  }),
});

export const watchFormFields: string[] = [
  'fulfillment_provider_id',
  'pounds',
  'ounces',
  'package',
  'service',
  'length',
  'width',
  'height',
  'provider',
  'shipping_speed',
];

export const validateShippingForm = (data, show) => {
  const strings = ['fulfillment_provider_id', 'package', 'service'];
  const numbers = ['pounds'];
  let dimensions = ['length', 'width', 'height'];
  if (show && !show.dimensions) {
    dimensions = [];
  }

  let valid = strings.every(x => !!data[x]);

  if (!valid) {
    return false;
  }

  valid = numbers.every(x => !!data[x] && +data[x] >= 0);

  if (!valid && +data.pounds + +(data.ounces || 0) === 0) {
    return false;
  }

  valid = dimensions.every(x => !!data[x] && +data[x] >= 0);

  return valid;
};

export const validatePlanLabelForm = data => {
  const strings = ['origin', 'destination'];

  const address = ['name', 'address_one', 'city', 'country', 'zip'];

  const numbers = ['pounds'];
  const dimensions = ['length', 'width', 'height'];

  let valid = strings.every(x => address.every(key => !!data[x][key]));

  if (!valid) {
    return false;
  }

  valid = numbers.every(x => !!data[x] && +data[x] >= 0);

  if (!valid && +data.pounds + +(data.ounces || 0) === 0) {
    return false;
  }

  valid = dimensions.every(x => !!data[x] && +data[x] >= 0);

  return valid;
};

const addressSchema = yup.object().shape({
  name: yup.string().trim().required('Name is required'),

  address_one: yup.string().trim().required('Address Line 1 is required'),

  address_two: yup
    .string()
    .trim()
    .nullable()
    .transform(value => value ?? '')
    .default('')
    .typeError('Address Line 2 must be a string'),

  city: yup.string().trim().required('City is required'),

  phone: yup.string().trim().required('Phone number is required'),

  state: yup
    .string()
    .trim()
    .when('country', (country, schema) => {
      if (country?.toLowerCase?.() === 'us') {
        return schema.required('State is required for US addresses');
      }
      return schema;
    }),

  country: yup.string().trim().required('Country is required'),

  zip: yup.string().trim().required('Zip Code is required'),
});

export const plainShippingSchema = yup
  .object()
  .shape(
    {
      origin: addressSchema,
      destination: addressSchema,
      reference_one: yup.string().nullable(),
      reference_two: yup.string().nullable(),
      reference_three: yup.string().nullable(),
      pounds: numberFromMasked
        .nullable()
        .notRequired()
        .min(0, 'Pounds must be ≥ 0'),
      ounces: numberFromMasked
        .nullable()
        .notRequired()
        .min(0, 'Ounces must be ≥ 0')
        .max(15, 'Ounces must be ≤ 15'),

      length: yup
        .number()
        .typeError('Must be a number')
        .min(1, 'Length must be at least 1 inch')
        .required('Length is required'),
      quantity: yup
        .number()
        .typeError('Must be a number')
        .min(1, 'No. of labels must be at least 1')
        .required('No. of labels is required'),
      width: yup
        .number()
        .typeError('Must be a number')
        .min(1, 'Width must be at least 1 inch')
        .required('Width is required'),
      height: yup
        .number()
        .typeError('Must be a number')
        .min(1, 'Height must be at least 1 inch')
        .required('Height is required'),

      shipping_speed: yup
        .string()
        .oneOf(['Standard', 'Priority', 'Expedited'], 'Invalid shipping speed')
        .required('Shipping speed is required'),

      shipment_value: yup
        .number()
        .typeError('Shipment Value must be a number')
        .positive('Shipment value must be positive')
        .max(999999, 'Shipment value too large')
        .test(
          'max-two-decimals',
          'Shipment value must have at most two decimal places',
          v => v === undefined || /^\d+(\.\d{1,2})?$/.test(String(v)),
        ),

      options: yup.object().shape({
        insurance: yup.string().typeError('Insurance value must be a string'),
      }),
    },
    // @ts-expect-error - TS2322: Type 'string' is not assignable to type '[string, string]'.
    ['ounces', 'pounds'],
  )
  .test('weight-required', 'Enter weight in lbs and/or oz.', function (v) {
    const L = Number(v?.pounds) || 0;
    const O = Number(v?.ounces) || 0;
    return L > 0 || O > 0 || this.createError({ path: 'pounds' });
  });

/*
 * Frames a row's marketplace icon. `manual` turns the org avatar fallback
 * (`.ui.image`) into a filled 33px disc.
 */
const ICON_MARKETPLACE = cn(
  'sk:rounded-[5px] sk:bg-transparent!',
  'sk:[&.manual]:p-0',
  'sk:[&.manual_.ui.image]:m-0 sk:[&.manual_.ui.image]:flex sk:[&.manual_.ui.image]:size-[33px] sk:[&.manual_.ui.image]:items-center sk:[&.manual_.ui.image]:justify-center sk:[&.manual_.ui.image]:rounded-full sk:[&.manual_.ui.image]:bg-fg sk:[&.manual_.ui.image]:p-0 sk:[&.manual_.ui.image]:text-surface',
);

export const IconMarketplace = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, ...rest }, ref) =>
  createElement('div', {
    ...rest,
    ref,
    className: cn(ICON_MARKETPLACE, className),
  }),
);
IconMarketplace.displayName = 'IconMarketplace';

export function getOrderError(orderError, isShipped) {
  if (isShipped) {
    return '';
  }

  return (orderError || [])
    .filter(error => !error.resolved && error.reason !== 'Invalid Address')
    .map(error => error.reason)
    .join(', ');
}

// If a connected marketplace has sub-marketplaces,
// show the sub-marketplaces instead of the main marketplace;
// otherwise, show the main marketplace.
export function getMarketplaceFilterOptions(
  marketplaceData: MarketplaceListItem[],
): MarketplaceListItem[] {
  return marketplaceData.filter(item => {
    const marketplace = item?.item;

    if (marketplace?.marketplace_connected === true) {
      if (marketplace?.is_main === true) {
        return false;
      }
    }

    if (marketplace?.marketplace_connected === false) {
      if (marketplace?.is_sub_marketplace === true) {
        return true;
      }

      return false;
    }

    return true;
  });
}
